import { useOutletContext } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import {
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
} from '@mui/material'
import { Description, Download } from '@mui/icons-material'

import { queries } from '../../graphql'
import { LinearProgress } from '../../components'

export function ViewFundDocuments() {
  const { fundId } = useOutletContext()

  const { data, loading, error } = useQuery(queries.funds.myFund, {
    variables: { fundId },
    skip: !fundId,
  })

  const fund = data?.me?.fund
  const taxReceiptYears = fund?.taxReceiptYears || []

  if (loading) {
    return <LinearProgress />
  }

  if (error) {
    return <Typography color="error">Error loading documents: {error.message}</Typography>
  }

  const handleDownload = async (year) => {
    try {
      const authState = JSON.parse(localStorage.getItem('givewise_auth_state') || '{}')
      const token = authState?.state?.rawToken

      const response = await fetch(
        `${process.env.REACT_APP_API_HOST || ''}/pdf/generate/tax-receipt/${fundId}/${year}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      if (!response.ok) {
        throw new Error('Failed to download receipt')
      }

      // Get the blob and create download link
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `tax-receipt-${year}.pdf`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (downloadError) {
      console.error('Error downloading tax receipt:', downloadError)
    }
  }

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Tax Receipts
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Download tax receipts for contributions made to this fund.
      </Typography>

      {taxReceiptYears.length === 0 ? (
        <Typography color="text.secondary">No tax receipts available</Typography>
      ) : (
        <List>
          {taxReceiptYears.map((year, index) => (
            <Box key={year}>
              {index > 0 && <Divider />}
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleDownload(year)}>
                  <ListItemIcon>
                    <Description color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={`Tax Receipt ${year}`} secondary="Click to download PDF" />
                  <Download color="action" />
                </ListItemButton>
              </ListItem>
            </Box>
          ))}
        </List>
      )}
    </Paper>
  )
}
